import { useCallback } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { deleteFolder, deleteMail, moveMail } from '@/services/api/endpoints';
import { moveFolderApi } from '@/services/api/folders';
import { addBreadcrumb } from '@/services/breadcrumbs/breadcrumbs';
import { reportError } from '@/services/errors/errorReporter';
import type { Folder } from '@/types/domain';
import { pruneRedundant, type BatchOutcome, type SelectedRef } from './selection';

/**
 * Les actions de LOT sur une multi-sélection : déplacer, supprimer.
 *
 * POURQUOI CE N'EST PAS UNE BOUCLE AUTOUR DES HOOKS EXISTANTS. Il n'existe aucun
 * endpoint de lot : `outlook-move-mail` et `outlook-move-folder` traitent un
 * élément par appel, et chacun peut être refusé INDIVIDUELLEMENT — un doublon dans
 * le dossier cible (409), un dossier déposé dans son propre descendant (cycle).
 * Le vrai travail n'est donc pas d'appeler N fois, c'est de rendre compte
 * fidèlement d'un résultat PARTIEL. Les hooks unitaires, eux, invalident et
 * affichent un toast à chaque appel : vingt toasts pour un geste.
 *
 * SÉQUENTIEL, ET NON EN PARALLÈLE. Deux déplacements simultanés vers le même
 * dossier peuvent se contredire (contrainte d'unicité du nom entre frères), et un
 * échec devient impossible à attribuer. Le coût est de N allers-retours, sur des
 * opérations qui ne touchent que la base.
 */

/** Résout le parent d'un dossier depuis les caches déjà chargés. */
function parentResolver(qc: QueryClient, containerIds: Iterable<string>): (id: string) => string | null {
  const parents = new Map<string, string | null>();
  for (const cid of containerIds) {
    for (const f of qc.getQueryData<Folder[]>(['folders', cid]) ?? []) {
      parents.set(f.id, f.parentId ?? null);
    }
  }
  // Cache absent = parent inconnu = `null`. L'élagage reste JUSTE dans ce cas :
  // il repose d'abord sur le `parentId` porté par l'élément sélectionné lui-même,
  // qui est toujours exact. Seul le cas « petit-fils d'un dossier sélectionné »
  // exige le cache — et un dossier ne se sélectionne que s'il est affiché, donc
  // son arborescence est chargée.
  return (id: string) => parents.get(id) ?? null;
}

export interface BatchMoveArgs {
  refs: SelectedRef[];
  /** `null` = racine de l'espace. Refusé pour les mails, cf. plus bas. */
  targetFolderId: string | null;
  targetContainerId: string;
}

export interface BatchResult {
  outcomes: BatchOutcome[];
  /**
   * Nombre d'éléments qui ont suivi un dossier sélectionné, sans appel propre.
   * Compté et DIT : sans cela, « 1 déplacé » pour cinq lignes cochées ressemble à
   * une perte.
   */
  carried: number;
}

export function useBatchActions() {
  const qc = useQueryClient();

  /** Une seule invalidation pour tout le lot, à la fin. */
  const invalidate = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['folders'] });
    void qc.invalidateQueries({ queryKey: ['mails'] });
    void qc.invalidateQueries({ queryKey: ['search'] });
  }, [qc]);

  const move = useCallback(
    async ({ refs, targetFolderId, targetContainerId }: BatchMoveArgs): Promise<BatchResult> => {
      const parentOf = parentResolver(
        qc,
        new Set([...refs.map((r) => r.containerId), targetContainerId]),
      );
      const kept = pruneRedundant(refs, parentOf);
      const carried = refs.length - kept.length;

      addBreadcrumb({
        category: 'mutation',
        message: 'batch-move start',
        data: { count: kept.length, carried, targetFolderId, targetContainerId },
      });

      const outcomes: BatchOutcome[] = [];
      for (const ref of kept) {
        try {
          if (ref.kind === 'mail') {
            if (!targetFolderId) {
              // `outlook-move-mail` exige un dossier (uuid). Ranger un mail à la
              // racine est possible à la CRÉATION, pas au déplacement : le dire
              // plutôt que de laisser croire à un échec technique.
              outcomes.push({
                ref,
                status: 'skipped',
                message: 'un mail ne se déplace pas à la racine d’un espace',
              });
              continue;
            }
            if (ref.parentId === targetFolderId) {
              outcomes.push({ ref, status: 'skipped', message: 'déjà dans ce dossier' });
              continue;
            }
            await moveMail({ mailId: ref.id, targetFolderId });
          } else {
            if (ref.id === targetFolderId) {
              outcomes.push({ ref, status: 'skipped', message: 'c’est la destination' });
              continue;
            }
            if (ref.parentId === targetFolderId) {
              outcomes.push({ ref, status: 'skipped', message: 'déjà à cet endroit' });
              continue;
            }
            await moveFolderApi({
              folderId: ref.id,
              newParentId: targetFolderId,
              newContainerId:
                ref.containerId !== targetContainerId ? targetContainerId : undefined,
            });
          }
          outcomes.push({ ref, status: 'done' });
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          // Journalisé une fois par élément, mais SANS interrompre le lot : les
          // suivants n'ont pas à payer l'échec du précédent.
          reportError({
            error: err instanceof Error ? err : new Error(message),
            tags: ['batch', 'move', ref.kind],
            isHandled: true,
          });
          outcomes.push({ ref, status: 'error', message });
        }
      }

      invalidate();
      return { outcomes, carried };
    },
    [invalidate, qc],
  );

  const remove = useCallback(
    async (refs: SelectedRef[]): Promise<BatchResult> => {
      const parentOf = parentResolver(qc, new Set(refs.map((r) => r.containerId)));
      // Même élagage qu'au déplacement, et pour une raison plus forte encore :
      // supprimer un dossier masque déjà tout son contenu (suppression en
      // cascade côté serveur). Supprimer ensuite un mail qui s'y trouvait
      // renverrait un 404 présenté comme un échec, alors que tout s'est bien passé.
      const kept = pruneRedundant(refs, parentOf);
      const carried = refs.length - kept.length;

      addBreadcrumb({
        category: 'mutation',
        message: 'batch-delete start',
        data: { count: kept.length, carried },
      });

      const outcomes: BatchOutcome[] = [];
      for (const ref of kept) {
        try {
          if (ref.kind === 'mail') await deleteMail(ref.id);
          else await deleteFolder(ref.id);
          outcomes.push({ ref, status: 'done' });
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          reportError({
            error: err instanceof Error ? err : new Error(message),
            tags: ['batch', 'delete', ref.kind],
            isHandled: true,
          });
          outcomes.push({ ref, status: 'error', message });
        }
      }

      invalidate();
      return { outcomes, carried };
    },
    [invalidate, qc],
  );

  return { move, remove };
}
